import { useNavigate, useParams } from 'react-router'
import PageWrapper from '../components/PageWrapper'
import LinkButton from '../components/LinkButton'
import { useEffect, useMemo, useState } from 'react'
import { fetcher } from '../utils/fetcher'
import Loader from '../components/Loader'
import { colors } from '../utils/config'
import { Title } from 'react-head'
import CourseEditorBox from '../components/CourseEditorBox'
import { Button, Icon } from '@bm-js/atlascloud-ui'
import Checkbox from '../components/Checkbox'
import Textarea from '../components/Textarea'
import ImageInput from '../components/ImageInput'
import { v4 as uuid } from 'uuid'
import CourseEditorExpandCard from '../components/CourseEditorExpandCard'
import CourseEditorPart from '../components/CourseEditorPart'
import KnowledgeTestEdit from '../components/KnowledgeTestEdit'
import {
  ChangeEvent,
  CourseBM,
  CoursePart,
  jsonCopy,
  LucideIconName,
} from '@bm-js/atlascloud-shared'
import { useToast, useToken, useWebsite } from '../utils/hooks'
import { useBeforeUnload } from '../hooks/useBeforeUnload'

// TODO: Translate

const CourseEditor = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const token = useToken()
  const { triggerRebuild } = useWebsite()

  const [course, setCourse] = useState<CourseBM>()
  const [courseImmut, setCourseImmut] = useState<CourseBM>()
  const [loading, setLoading] = useState(true)

  const getCourse = async () => {
    setLoading(true)
    const { err, data } = await fetcher<CourseBM>({
      path: `courses/${id}`,
      auth: token,
    })
    if (err) toast.error('Kunde inte hämta utbildning')
    else {
      setCourse(data)
      setCourseImmut(jsonCopy(data))
    }
    setLoading(false)
  }

  const saveCourse = async () => {
    setLoading(true)
    const { err } = await fetcher({
      path: `courses/${id}`,
      auth: token,
      method: 'POST',
      body: course,
    })
    if (err) toast.error('Kunde inte spara utbildning')
    else {
      toast.success('Utbildning sparad')
      await getCourse()
      await triggerRebuild()
    }
    setLoading(false)
  }

  const handleChange = (e: ChangeEvent) => {
    setCourse({
      ...course!,
      [e.target.name]: e.target.value,
    })
  }

  const setParts = (parts: CoursePart[]) => {
    setCourse({
      ...course!,
      parts,
    })
  }

  const addPart = () => {
    const updated = [...course!.parts]
    updated.push({
      name: 'Nytt avsnitt',
      uuid: uuid(),
      contents: [],
    })
    setParts(updated)
  }

  const isChanged = useMemo(() => {
    if (!course || !courseImmut) return false
    return JSON.stringify(course) !== JSON.stringify(courseImmut)
  }, [course, courseImmut])

  const leaveEditor = () => {
    if (isChanged) {
      const proceed = confirm(
        'Är du säker på att du vill lämna redigeraren? Har du sparat dina ändringar?'
      )
      if (proceed) navigate('/courses')
      else return
    } else navigate('/courses')
  }

  useEffect(() => {
    // eslint-disable-next-line
    getCourse()
    // eslint-disable-next-line
  }, [id])

  useBeforeUnload(isChanged, 'Har du sparat dina ändringar?')

  if (loading)
    return (
      <div className='flex min-h-[400px] items-center justify-center'>
        <Loader color={colors.primary400} />
      </div>
    )

  return (
    <div className='pb-80'>
      <Title>{course!.name} | Atlas Cloud</Title>
      <PageWrapper>
        <LinkButton
          icon={LucideIconName.ArrowLeft}
          reverse
          onClick={leaveEditor}
        >
          Tillbaka
        </LinkButton>
        <div className='mb-6 flex items-end justify-between'>
          <h1 className='m-0 mt-2 text-4xl text-primary-400'>
            Redigera utbildning
          </h1>
          <Button disabled={!isChanged} onClick={saveCourse}>
            {isChanged ? 'Spara' : 'Sparad'}
          </Button>
        </div>
        <h2 className='mt-6 mb-2'>Grundinformation</h2>
        <CourseEditorBox className='relative'>
          <input
            value={course!.name}
            name='name'
            onChange={handleChange}
            className='w-full bg-transparent outline-none'
          />

          <Icon
            name={LucideIconName.Pen}
            className='pointer-events-none absolute right-8 top-5 h-5 w-5'
          />
        </CourseEditorBox>
        <CourseEditorBox className='flex items-center justify-between'>
          <p>Synlighet: {course!.published ? 'Publicerad' : 'Ej publicerad'}</p>
          <Checkbox
            noMargin
            checked={course!.published}
            name='published'
            onChange={(e) =>
              setCourse({ ...course!, published: e.target.checked })
            }
          />
        </CourseEditorBox>
        <CourseEditorExpandCard header='Bild och beskrivning'>
          <ImageInput
            label='Bild'
            desc='Visas på utbildningskortet samt i början av utbildningen'
            file={course!.thumbnail}
            onChange={(f) => setCourse({ ...course!, thumbnail: f })}
          />
          <Textarea
            value={(course!.description || []).join('\n')}
            onChange={(e) =>
              setCourse({ ...course!, description: e.target.value.split('\n') })
            }
            label='Beskrivning'
            desc='Visas på utbildningskortet samt i början av utbildningen'
          />
        </CourseEditorExpandCard>
        <h2 className='mt-6 mb-2'>Avsnitt</h2>
        {course!.parts.map((part, i) => (
          <CourseEditorPart
            key={part.uuid}
            part={part}
            index={i}
            setParts={setParts}
            parts={course!.parts}
          />
        ))}
        <div className='flex justify-end'>
          <Button onClick={addPart}>Lägg till avsnitt</Button>
        </div>
        <KnowledgeTestEdit course={course!} setCourse={setCourse} />
      </PageWrapper>
    </div>
  )
}

export default CourseEditor
